import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Loader2, Building2, Camera as Instagram, Briefcase as Linkedin, MessageSquare, Briefcase, Star, ThumbsUp, MessageCircle as Twitter, Target, CheckCircle2, AlertTriangle, Lightbulb, FileText, ArrowRight, Zap, XCircle, History as HistoryIcon, X } from 'lucide-react';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { api } from '../lib/gapApi';
import { MarketMatrix } from '../components/MarketMatrix';
import { HistoricalTrends } from '../components/HistoricalTrends';

const AnalysisCard = ({ title, icon: Icon, mainResult, compResult, showCompetitor, colorTheme, onGeneratePlan }) => {
  const [activeTab, setActiveTab] = useState('main');
  const hasCompetitor = Boolean(showCompetitor);

  if (!mainResult && !compResult) return null;

  const getSeverityColor = (severity) => {
    const s = (severity || '').toLowerCase();
    if (s === 'high') return 'text-red-400 bg-red-400/10 border-red-400/30';
    if (s === 'medium') return 'text-orange-400 bg-orange-400/10 border-orange-400/30';
    return 'text-blue-400 bg-blue-400/10 border-blue-400/30';
  };

  const activeResult = activeTab === 'main' ? mainResult : compResult;

  return (
    <div className={`group relative bg-slate-900/40 backdrop-blur-xl rounded-3xl p-8 border border-slate-700/50 flex flex-col h-full transition-all duration-300 hover:-translate-y-2 ${colorTheme.hoverBorder} ${colorTheme.hoverShadow}`}>
      <div className={`absolute inset-0 ${colorTheme.bgGradient} opacity-0 group-hover:opacity-100 transition-opacity rounded-3xl`} />
      <div className="relative z-10 flex flex-col gap-4 mb-6 pb-4 border-b border-slate-700/50">
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-2xl border ${colorTheme.iconBg} ${colorTheme.iconText} ${colorTheme.iconBorder} group-hover:scale-110 transition-transform`}>
            <Icon size={28} />
          </div>
          <h2 className={`text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r ${colorTheme.textGradient}`}>{title}</h2>
        </div>

        {hasCompetitor && (
          <div className="flex bg-slate-800/50 rounded-lg p-1 mt-2">
            <button
              onClick={() => setActiveTab('main')}
              type="button"
              className={`flex-1 text-sm py-1.5 rounded-md transition-all ${activeTab === 'main' ? 'bg-slate-700 text-white shadow-sm' : 'text-slate-400 hover:text-slate-200'}`}
            >
              Target Company
            </button>
            <button
              onClick={() => setActiveTab('comp')}
              type="button"
              className={`flex-1 text-sm py-1.5 rounded-md transition-all ${activeTab === 'comp' ? 'bg-red-500/20 text-red-400 shadow-sm border border-red-500/30' : 'text-slate-400 hover:text-slate-200'}`}
            >
              Competitor
            </button>
          </div>
        )}
      </div>
      <div className="relative z-10 flex-1 overflow-y-auto custom-scrollbar max-h-96 pr-4">
        {activeResult ? (
          <div className="space-y-6">
            <p className={`text-slate-300 text-sm leading-relaxed italic border-l-2 pl-3 ${colorTheme.borderLeft}`}>
              {activeResult.gap_analysis?.executive_summary}
            </p>
            <div className="space-y-5">
              {activeResult.gap_analysis?.identified_gaps?.slice(0, 3).map((gap, idx) => (
                <div key={idx} className={`bg-slate-800/60 p-4 rounded-xl border border-slate-700/50 transition-colors flex flex-col gap-3 ${colorTheme.cardHoverBorder}`}>
                  <div className="flex justify-between items-start gap-3">
                    <h4 className="font-bold text-slate-100 text-sm leading-tight">{gap.title}</h4>
                    <span className={`shrink-0 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-md border ${getSeverityColor(gap.severity)}`}>
                      {gap.severity}
                    </span>
                  </div>
                  {activeTab === 'main' && (
                    <button
                      onClick={() => onGeneratePlan(gap)}
                      className="mt-2 text-xs font-bold text-emerald-400 hover:text-emerald-300 flex items-center gap-1 transition-colors bg-emerald-500/10 py-1.5 px-3 rounded-lg w-fit border border-emerald-500/20 hover:border-emerald-500/40"
                    >
                      <Zap size={12} /> Fix this Gap
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-slate-500 italic text-center mt-10">Analysis failed or unavailable.</p>
        )}
      </div>
    </div>
  );
};

export const CompanyAnalysis = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const historyId = searchParams.get('historyId');
  const [isReplay, setIsReplay] = useState(false);
  const [loadingReplay, setLoadingReplay] = useState(false);
  // id of the /new-analysis row this run is saved as (set after POST /new-analysis/save,
  // or to historyId on replay) — lets handleCombine/handleGenerateProposal PATCH it later.
  const [savedAnalysisId, setSavedAnalysisId] = useState(null);

  const [companyName, setCompanyName] = useState('');
  const [website, setWebsite] = useState('');

  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [progress, setProgress] = useState({
    general: 'idle', instagram: 'idle', linkedin: 'idle', reddit: 'idle', glassdoor: 'idle', g2: 'idle', trustpilot: 'idle', twitter: 'idle'
  });
  const [gapResult, setGapResult] = useState(null);

  const [showCompetitor, setShowCompetitor] = useState(false);
  const [competitorName, setCompetitorName] = useState('');
  const [competitorWebsiteUrl, setCompetitorWebsiteUrl] = useState('');

  const [compGapResult, setCompGapResult] = useState(null);
  const [compInstagramResult, setCompInstagramResult] = useState(null);
  const [compLinkedinResult, setCompLinkedinResult] = useState(null);
  const [compRedditResult, setCompRedditResult] = useState(null);
  const [compGlassdoorResult, setCompGlassdoorResult] = useState(null);
  const [compG2Result, setCompG2Result] = useState(null);
  const [compTrustpilotResult, setCompTrustpilotResult] = useState(null);
  const [compTwitterResult, setCompTwitterResult] = useState(null);
  const [instagramResult, setInstagramResult] = useState(null);
  const [linkedinResult, setLinkedinResult] = useState(null);
  const [redditResult, setRedditResult] = useState(null);
  const [glassdoorResult, setGlassdoorResult] = useState(null);
  const [g2Result, setG2Result] = useState(null);
  const [trustpilotResult, setTrustpilotResult] = useState(null);
  const [twitterResult, setTwitterResult] = useState(null);

  const [isCombining, setIsCombining] = useState(false);
  const [deepInsightResult, setDeepInsightResult] = useState(null);

  const [isProposing, setIsProposing] = useState(false);
  const [proposalResult, setProposalResult] = useState(null);
  const [, setEditedProposalText] = useState('');

  const [error, setError] = useState('');

  const [activeGapForPlan, setActiveGapForPlan] = useState(null);
  const [isGeneratingPlan, setIsGeneratingPlan] = useState(false);
  const [actionPlanResult, setActionPlanResult] = useState(null);

  // Replay mode: Dashboard's "Recent Analyses" links here with ?historyId=<id> —
  // load the saved combined result instead of running the 8 live analyses again.
  useEffect(() => {
    if (!historyId) return;
    let cancelled = false;
    setLoadingReplay(true);
    setError('');
    api.get(`/new-analysis/history/${historyId}`)
      .then(({ data }) => {
        if (cancelled) return;
        const r = data.result || {};
        setCompanyName(data.company_name || r.companyName || '');
        setWebsite(data.website || r.website || '');
        setShowCompetitor(!!r.showCompetitor);
        setCompetitorName(r.competitorName || '');
        setCompetitorWebsiteUrl(r.competitorWebsiteUrl || '');
        setGapResult(r.gapResult || null);
        setInstagramResult(r.instagramResult || null);
        setLinkedinResult(r.linkedinResult || null);
        setRedditResult(r.redditResult || null);
        setGlassdoorResult(r.glassdoorResult || null);
        setG2Result(r.g2Result || null);
        setTrustpilotResult(r.trustpilotResult || null);
        setTwitterResult(r.twitterResult || null);
        setCompGapResult(r.compGapResult || null);
        setCompInstagramResult(r.compInstagramResult || null);
        setCompLinkedinResult(r.compLinkedinResult || null);
        setCompRedditResult(r.compRedditResult || null);
        setCompGlassdoorResult(r.compGlassdoorResult || null);
        setCompG2Result(r.compG2Result || null);
        setCompTrustpilotResult(r.compTrustpilotResult || null);
        setCompTwitterResult(r.compTwitterResult || null);
        setDeepInsightResult(r.deepInsightResult || null);
        setProposalResult(r.proposalResult || null);
        setIsReplay(true);
        setSavedAnalysisId(historyId);
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.detail || 'Failed to load saved analysis.');
      })
      .finally(() => { if (!cancelled) setLoadingReplay(false); });
    return () => { cancelled = true; };
  }, [historyId]);

  const handleCloseReplay = () => {
    setSearchParams({}, { replace: true });
    setIsReplay(false);
    setSavedAnalysisId(null);
    setCompanyName('');
    setWebsite('');
    setShowCompetitor(false);
    setCompetitorName('');
    setCompetitorWebsiteUrl('');
    setGapResult(null);
    setInstagramResult(null);
    setLinkedinResult(null);
    setRedditResult(null);
    setGlassdoorResult(null);
    setG2Result(null);
    setTrustpilotResult(null);
    setTwitterResult(null);
    setCompGapResult(null);
    setCompInstagramResult(null);
    setCompLinkedinResult(null);
    setCompRedditResult(null);
    setCompGlassdoorResult(null);
    setCompG2Result(null);
    setCompTrustpilotResult(null);
    setCompTwitterResult(null);
    setDeepInsightResult(null);
    setProposalResult(null);
  };

  const calculateScores = () => {
    const getScore = (result) => {
      if (!result) return 0;

      let penalty = 0;
      let hasData = false;

      if (result.gap_analysis?.identified_gaps) {
        hasData = true;
        result.gap_analysis.identified_gaps.forEach((g) => {
          if (g.severity?.toLowerCase() === 'high') penalty += 15;
          else if (g.severity?.toLowerCase() === 'medium') penalty += 10;
          else penalty += 5;
        });
      }

      if (result.sentiment_analysis?.average_sentiment_score !== undefined) {
        hasData = true;
        penalty += (10 - (result.sentiment_analysis.average_sentiment_score * 10));
      }

      if (!hasData) return 0;
      return Math.max(10, 100 - penalty);
    };

    return [
      { subject: 'Brand Presence', A: getScore(instagramResult) || getScore(twitterResult) || 0, B: getScore(compInstagramResult) || getScore(compTwitterResult) || 0, fullMark: 100 },
      { subject: 'Employee Satisfaction', A: getScore(glassdoorResult) || 0, B: getScore(compGlassdoorResult) || 0, fullMark: 100 },
      { subject: 'Customer Happiness', A: getScore(trustpilotResult) || getScore(g2Result) || 0, B: getScore(compTrustpilotResult) || getScore(compG2Result) || 0, fullMark: 100 },
      { subject: 'Organic SEO', A: getScore(gapResult) || 0, B: getScore(compGapResult) || 0, fullMark: 100 },
      { subject: 'Community Trust', A: getScore(redditResult) || getScore(linkedinResult) || 0, B: getScore(compRedditResult) || getScore(compLinkedinResult) || 0, fullMark: 100 },
    ];
  };

  const ProgressItem = ({ label, status }) => (
    <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700/50">
      <span className="text-sm font-medium text-slate-300">{label}</span>
      {status === 'loading' && <Loader2 size={16} className="text-emerald-400 animate-spin" />}
      {status === 'done' && <CheckCircle2 size={16} className="text-emerald-500" />}
      {status === 'error' && <XCircle size={16} className="text-red-500" />}
      {status === 'idle' && <div className="w-4 h-4 rounded-full border-2 border-slate-600" />}
    </div>
  );

  const handleGenerateActionPlan = async (gap) => {
    setActiveGapForPlan(gap);
    setIsGeneratingPlan(true);
    setActionPlanResult(null);

    try {
      await new Promise(r => setTimeout(r, 2000));
      setActionPlanResult(
        "### Action Plan: " + gap.title + "\n\n" +
        "1. **Immediate Audit:** Assign a team member to investigate the root cause.\n" +
        "2. **Targeted Campaign:** Launch an internal or external campaign specifically addressing this " + gap.severity + " severity issue.\n" +
        "3. **Feedback Loop:** Set up a weekly review to monitor the metrics related to this gap.\n\n" +
        "*AI Suggestion: Because this is a " + gap.severity + " issue, prioritize it in the next sprint.*"
      );
    } catch (e) {
      setActionPlanResult("Failed to generate plan.");
    } finally {
      setIsGeneratingPlan(false);
    }
  };

  const handleAnalyze = async (e) => {
    e.preventDefault();
    if (!companyName || !website) {
      setError('Please provide both company name and website URL.');
      return;
    }
    if (showCompetitor && (!competitorName || !competitorWebsiteUrl)) {
      setError("Please provide the competitor's name and website URL.");
      return;
    }

    setSearchParams({}, { replace: true });
    setIsReplay(false);
    setSavedAnalysisId(null);
    setIsAnalyzing(true);
    setError('');
    setGapResult(null);
    setInstagramResult(null);
    setLinkedinResult(null);
    setRedditResult(null);
    setGlassdoorResult(null);
    setG2Result(null);
    setTrustpilotResult(null);
    setTwitterResult(null);
    setDeepInsightResult(null);
    setProposalResult(null);

    setCompGapResult(null);
    setCompInstagramResult(null);
    setCompLinkedinResult(null);
    setCompRedditResult(null);
    setCompGlassdoorResult(null);
    setCompG2Result(null);
    setCompTrustpilotResult(null);
    setCompTwitterResult(null);

    const payload = {
      company_name: companyName,
      website_url: website,
    };

    const compPayload = {
      company_name: competitorName,
      website_url: competitorWebsiteUrl,
    };

    try {
      const promises = [
        api.post('/gap-analysis/analyze', payload),
        api.post('/social/instagram/analyze', payload),
        api.post('/social/linkedin/analyze', payload),
        api.post('/social/reddit/analyze', payload),
        api.post('/social/glassdoor/analyze', payload),
        api.post('/social/g2/analyze', payload),
        api.post('/social/trustpilot/analyze', payload),
        api.post('/social/twitter/analyze', payload)
      ];

      const hasCompetitor = showCompetitor && competitorName && competitorWebsiteUrl;
      if (hasCompetitor) {
        promises.push(
          api.post('/gap-analysis/analyze', compPayload),
          api.post('/social/instagram/analyze', compPayload),
          api.post('/social/linkedin/analyze', compPayload),
          api.post('/social/reddit/analyze', compPayload),
          api.post('/social/glassdoor/analyze', compPayload),
          api.post('/social/g2/analyze', compPayload),
          api.post('/social/trustpilot/analyze', compPayload),
          api.post('/social/twitter/analyze', compPayload)
        );
      }

      const results = await Promise.allSettled(promises);

      const [gap, insta, linkedin, reddit, glassdoor, g2, trustpilot, twitter] = results.slice(0, 8);

      if (gap.status === 'fulfilled') setGapResult(gap.value.data);
      if (insta.status === 'fulfilled') setInstagramResult(insta.value.data);
      if (linkedin.status === 'fulfilled') setLinkedinResult(linkedin.value.data);
      if (reddit.status === 'fulfilled') setRedditResult(reddit.value.data);
      if (glassdoor.status === 'fulfilled') setGlassdoorResult(glassdoor.value.data);
      if (g2.status === 'fulfilled') setG2Result(g2.value.data);
      if (trustpilot.status === 'fulfilled') setTrustpilotResult(trustpilot.value.data);
      if (twitter.status === 'fulfilled') setTwitterResult(twitter.value.data);

      if (hasCompetitor) {
        const [cGap, cInsta, cLinkedin, cReddit, cGlassdoor, cG2, cTrustpilot, cTwitter] = results.slice(8, 16);
        if (cGap.status === 'fulfilled') setCompGapResult(cGap.value.data);
        if (cInsta.status === 'fulfilled') setCompInstagramResult(cInsta.value.data);
        if (cLinkedin.status === 'fulfilled') setCompLinkedinResult(cLinkedin.value.data);
        if (cReddit.status === 'fulfilled') setCompRedditResult(cReddit.value.data);
        if (cGlassdoor.status === 'fulfilled') setCompGlassdoorResult(cGlassdoor.value.data);
        if (cG2.status === 'fulfilled') setCompG2Result(cG2.value.data);
        if (cTrustpilot.status === 'fulfilled') setCompTrustpilotResult(cTrustpilot.value.data);
        if (cTwitter.status === 'fulfilled') setCompTwitterResult(cTwitter.value.data);
      }

      if (gap.status === 'rejected' && insta.status === 'rejected' && linkedin.status === 'rejected') {
        setError('All analyses failed. Please check the backend logs.');
      }
    } catch (err) {
      setError('An unexpected error occurred.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleCombine = async () => {
    setIsCombining(true);
    try {
      const response = await api.post('/analysis/deep-insight', {
        company_name: companyName,
        gap_result: gapResult || {},
        instagram_result: instagramResult || {},
        linkedin_result: linkedinResult || {},
        reddit_result: redditResult || {},
        glassdoor_result: glassdoorResult || {},
        g2_result: g2Result || {},
        trustpilot_result: trustpilotResult || {},
        twitter_result: twitterResult || {},
        competitor_name: (showCompetitor && competitorName) ? competitorName : null,
        comp_gap_result: compGapResult,
        comp_instagram_result: compInstagramResult,
        comp_linkedin_result: compLinkedinResult,
        comp_reddit_result: compRedditResult,
        comp_glassdoor_result: compGlassdoorResult,
        comp_g2_result: compG2Result,
        comp_trustpilot_result: compTrustpilotResult,
        comp_twitter_result: compTwitterResult
      });
      setDeepInsightResult(response.data);
      if (savedAnalysisId) {
        api.patch(`/new-analysis/${savedAnalysisId}`, { result: { deepInsightResult: response.data } })
          .catch(e => console.error("Failed to update saved analysis with deep insight", e));
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate deep insight.');
    } finally {
      setIsCombining(false);
    }
  };

  const handleGenerateProposal = async () => {
    setIsProposing(true);
    try {
      const response = await api.post('/analysis/generate-proposal', {
        company_name: companyName,
        deep_insight_result: deepInsightResult
      });
      setProposalResult(response.data);
      setEditedProposalText(response.data.proposal);
      if (savedAnalysisId) {
        api.patch(`/new-analysis/${savedAnalysisId}`, { result: { proposalResult: response.data } })
          .catch(e => console.error("Failed to update saved analysis with proposal", e));
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate proposal.');
    } finally {
      setIsProposing(false);
    }
  };

  const isAllAnalysesDone = (gapResult || instagramResult || linkedinResult || redditResult || glassdoorResult || g2Result || trustpilotResult || twitterResult) && !isAnalyzing;

  useEffect(() => {
    if (isAllAnalysesDone && companyName && !isReplay) {
      const scores = calculateScores();
      api.post('/trends', {
        company_name: companyName,
        brand_score: scores[0].A,
        employee_score: scores[1].A,
        customer_score: scores[2].A,
        seo_score: scores[3].A,
        community_score: scores[4].A
      }).catch(e => console.error("Failed to save trends", e));

      api.post('/new-analysis/save', {
        company_name: companyName,
        website,
        result: {
          companyName, website, showCompetitor, competitorName, competitorWebsiteUrl,
          gapResult, instagramResult, linkedinResult, redditResult, glassdoorResult, g2Result, trustpilotResult, twitterResult,
          compGapResult, compInstagramResult, compLinkedinResult, compRedditResult, compGlassdoorResult, compG2Result, compTrustpilotResult, compTwitterResult,
        }
      }).then(({ data }) => setSavedAnalysisId(data.id))
        .catch(e => console.error("Failed to save new-analysis history", e));
    }
  }, [isAllAnalysesDone]);

  const getSeverityColor = (severity) => {
    const s = (severity || '').toLowerCase();
    if (s === 'high') return 'text-red-400 bg-red-400/10 border-red-400/30';
    if (s === 'medium') return 'text-orange-400 bg-orange-400/10 border-orange-400/30';
    return 'text-blue-400 bg-blue-400/10 border-blue-400/30';
  };

  return (
    <div className="min-h-screen p-6 md:p-12 relative overflow-hidden">
      {/* Background Decor */}
      {/* <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-600/20 rounded-full blur-[120px] pointer-events-none"></div> */}
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-indigo-600/20 rounded-full blur-[120px] pointer-events-none"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-12 mt-4">
          <h1 className="text-4xl md:text-5xl font-extrabold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-500">
            Comprehensive Company Analysis
          </h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Run complete diagnostics across general presence, Instagram, and LinkedIn. Synthesize results and automatically generate tailored proposals.
          </p>
        </div>

        {/* Input Form */}
        <div className="bg-slate-900/60 backdrop-blur-xl p-8 rounded-2xl border border-slate-700/50 shadow-xl mb-12">
          <form onSubmit={handleAnalyze} className="flex flex-col gap-6">
            <div className="flex flex-col md:flex-row gap-6 w-full">
              <div className="flex-1 w-full">
                <label className="block text-sm font-medium text-slate-300 mb-2">Company Name *</label>
                <div className="relative">
                  <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={20} />
                  <input
                    type="text"
                    required
                    className="w-full bg-slate-900/50 border border-slate-700/50 text-white rounded-xl pl-12 pr-4 py-4 focus:outline-none focus:border-emerald-500/50 focus:ring-2 focus:ring-emerald-500/20 transition-all placeholder:text-slate-600 shadow-inner"
                    placeholder="e.g. Acme Corp"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex-1 w-full">
                <label className="block text-sm font-medium text-slate-300 mb-2 uppercase tracking-wider text-xs">Website URL *</label>
                <div className="relative group">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-emerald-400 transition-colors" size={20} />
                  <input
                    type="url"
                    required
                    className="w-full bg-slate-900/50 border border-slate-700/50 text-white rounded-xl pl-12 pr-4 py-4 focus:outline-none focus:border-emerald-500/50 focus:ring-2 focus:ring-emerald-500/20 transition-all placeholder:text-slate-600 shadow-inner"
                    placeholder="e.g. https://acme.com"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {showCompetitor && (
              <div className="flex-1 w-full flex flex-col gap-6 animate-in slide-in-from-top-4 mt-6 md:mt-0 pt-6 border-t border-slate-700/50 md:border-t-0 md:pt-0">
                <div>
                  <label className="block text-sm font-medium text-red-400 mb-2">Competitor Name *</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="h-5 w-5 text-red-400/50" />
                    </div>
                    <input
                      type="text"
                      className="block w-full pl-10 pr-3 py-3 border border-red-500/30 rounded-xl bg-slate-800/50 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      placeholder="e.g. Rival Corp"
                      value={competitorName}
                      onChange={(e) => setCompetitorName(e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-red-400 mb-2">Competitor Website URL *</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="h-5 w-5 text-red-400/50" />
                    </div>
                    <input
                      type="url"
                      className="block w-full pl-10 pr-3 py-3 border border-red-500/30 rounded-xl bg-slate-800/50 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      placeholder="https://rivalcorp.com"
                      value={competitorWebsiteUrl}
                      onChange={(e) => setCompetitorWebsiteUrl(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="w-full flex flex-col md:flex-row justify-between items-center mt-6 gap-4">
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => setShowCompetitor(!showCompetitor)}
                  className="text-slate-400 hover:text-white flex items-center gap-2 text-sm transition-colors"
                >
                  {showCompetitor ? <><XCircle size={16} /> Remove Competitor</> : <><Search size={16} /> Add Competitor Benchmarking</>}
                </button>
              </div>

              <button
                type="submit"
                disabled={isAnalyzing || !companyName || !website}
                className="w-full md:w-auto h-[58px] px-10 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-3 shadow-[0_10px_20px_rgba(16,185,129,0.2)] hover:shadow-[0_15px_30px_rgba(16,185,129,0.3)] hover:-translate-y-1"
              >
                {isAnalyzing ? (
                  <><Loader2 className="animate-spin" size={20} /> Analyzing...</>
                ) : (
                  <>Run All Analyses <ArrowRight size={18} /></>
                )}
              </button>
            </div>
          </form>
          {error && <p className="text-red-400 text-sm mt-4 text-center bg-red-400/10 py-2 rounded-lg border border-red-400/20">{error}</p>}
        </div>

        {historyId && (
          <div className="mb-12 p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl flex items-center justify-between gap-3 animate-in fade-in">
            <span className="text-sm text-blue-300 flex items-center gap-2">
              <HistoryIcon size={16} /> {loadingReplay ? 'Loading saved analysis...' : 'Viewing a saved analysis from history.'}
            </span>
            <button
              type="button"
              onClick={handleCloseReplay}
              className="text-xs font-bold text-blue-300 hover:text-white flex items-center gap-1 transition-colors"
            >
              <X size={14} /> Close
            </button>
          </div>
        )}

        {/* Progress Tracker Modal / Inline */}
        {isAnalyzing && (
          <div className="mb-12 bg-slate-900/60 backdrop-blur-xl rounded-3xl p-8 border border-emerald-500/30 shadow-[0_20px_40px_rgba(16,185,129,0.1)] animate-in fade-in slide-in-from-bottom-4">
            <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-3">
              <Loader2 className="animate-spin text-emerald-400" /> Live Analysis Engine Running...
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <ProgressItem label="General SEO" status={progress.general} />
              <ProgressItem label="Instagram" status={progress.instagram} />
              <ProgressItem label="LinkedIn" status={progress.linkedin} />
              <ProgressItem label="Reddit" status={progress.reddit} />
              <ProgressItem label="Glassdoor" status={progress.glassdoor} />
              <ProgressItem label="G2" status={progress.g2} />
              <ProgressItem label="Trustpilot" status={progress.trustpilot} />
              <ProgressItem label="Twitter / X" status={progress.twitter} />
            </div>
          </div>
        )}

        {/* Visual Sentiment Chart */}
        {isAllAnalysesDone && (
          <div className="mb-12 bg-slate-900/40 backdrop-blur-xl rounded-3xl p-8 border border-slate-700/50 flex flex-col md:flex-row items-center gap-8 animate-in fade-in slide-in-from-bottom-8">
            <div className="flex-1 w-full max-w-md h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart cx="50%" cy="50%" outerRadius="80%" data={calculateScores()}>
                  <PolarGrid stroke="#334155" />
                  <PolarAngleAxis dataKey="subject" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                  <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} axisLine={false} />
                  <Radar name={companyName || 'Company Sentiment'} dataKey="A" stroke="#10b981" strokeWidth={2} fill="#10b981" fillOpacity={0.3} />
                  {showCompetitor && <Radar name={competitorName || 'Competitor Sentiment'} dataKey="B" stroke="#ef4444" strokeWidth={2} fill="#ef4444" fillOpacity={0.3} />}
                  <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#f8fafc' }} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1">
              <h2 className="text-3xl font-extrabold text-white mb-4">Multi-Platform Sentiment Overview</h2>
              <p className="text-slate-400 mb-6 leading-relaxed">
                The visual chart aggregates AI sentiment analysis across all 8 specialized data sources.
                Areas closer to the outer edge represent highly positive brand perception, strong customer satisfaction, and excellent employee engagement.
                Areas pulling towards the center highlight critical operational or reputational gaps that require immediate attention.
              </p>
              <button onClick={handleCombine} disabled={isCombining} className="inline-flex items-center space-x-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold py-4 px-8 rounded-xl shadow-[0_10px_20px_rgba(16,185,129,0.2)] hover:shadow-[0_15px_30px_rgba(16,185,129,0.3)] transition-all disabled:opacity-50">
                {isCombining ? <><Loader2 className="animate-spin" size={20} /> <span>Synthesizing Deep Insight...</span></> : <><span>Generate Combined Deep Insight</span><Star size={20} /></>}
              </button>
            </div>
          </div>
        )}

        {/* 2-Column or 3-Column Analysis Results to avoid congestion */}
        {isAllAnalysesDone && (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8 mb-12 animate-in fade-in slide-in-from-bottom-8">
            <AnalysisCard
              title="General Gap" icon={Target} mainResult={gapResult} compResult={compGapResult} showCompetitor={showCompetitor} onGeneratePlan={handleGenerateActionPlan}
              colorTheme={{
                hoverBorder: 'hover:border-indigo-500/50', hoverShadow: 'hover:shadow-[0_20px_40px_rgba(99,102,241,0.1)]',
                bgGradient: 'bg-gradient-to-br from-indigo-500/5 to-transparent', iconBg: 'bg-indigo-500/10', iconText: 'text-indigo-400', iconBorder: 'border-indigo-500/20',
                textGradient: 'from-indigo-400 to-purple-400', borderLeft: 'border-indigo-500/50', cardHoverBorder: 'hover:border-indigo-500/30'
              }}
            />
            <AnalysisCard
              title="Instagram" icon={Instagram} mainResult={instagramResult} compResult={compInstagramResult} showCompetitor={showCompetitor} onGeneratePlan={handleGenerateActionPlan}
              colorTheme={{
                hoverBorder: 'hover:border-fuchsia-500/50', hoverShadow: 'hover:shadow-[0_20px_40px_rgba(217,70,239,0.1)]',
                bgGradient: 'bg-gradient-to-br from-fuchsia-500/5 to-transparent', iconBg: 'bg-fuchsia-500/10', iconText: 'text-fuchsia-400', iconBorder: 'border-fuchsia-500/20',
                textGradient: 'from-fuchsia-400 to-pink-400', borderLeft: 'border-fuchsia-500/50', cardHoverBorder: 'hover:border-fuchsia-500/30'
              }}
            />
            <AnalysisCard
              title="LinkedIn" icon={Linkedin} mainResult={linkedinResult} compResult={compLinkedinResult} showCompetitor={showCompetitor} onGeneratePlan={handleGenerateActionPlan}
              colorTheme={{
                hoverBorder: 'hover:border-blue-500/50', hoverShadow: 'hover:shadow-[0_20px_40px_rgba(59,130,246,0.1)]',
                bgGradient: 'bg-gradient-to-br from-blue-500/5 to-transparent', iconBg: 'bg-blue-500/10', iconText: 'text-blue-400', iconBorder: 'border-blue-500/20',
                textGradient: 'from-blue-400 to-cyan-400', borderLeft: 'border-blue-500/50', cardHoverBorder: 'hover:border-blue-500/30'
              }}
            />
            <AnalysisCard
              title="Reddit" icon={MessageSquare} mainResult={redditResult} compResult={compRedditResult} showCompetitor={showCompetitor} onGeneratePlan={handleGenerateActionPlan}
              colorTheme={{
                hoverBorder: 'hover:border-orange-500/50', hoverShadow: 'hover:shadow-[0_20px_40px_rgba(249,115,22,0.1)]',
                bgGradient: 'bg-gradient-to-br from-orange-500/5 to-transparent', iconBg: 'bg-orange-500/10', iconText: 'text-orange-400', iconBorder: 'border-orange-500/20',
                textGradient: 'from-orange-400 to-amber-400', borderLeft: 'border-orange-500/50', cardHoverBorder: 'hover:border-orange-500/30'
              }}
            />
            <AnalysisCard
              title="Glassdoor" icon={Briefcase} mainResult={glassdoorResult} compResult={compGlassdoorResult} showCompetitor={showCompetitor} onGeneratePlan={handleGenerateActionPlan}
              colorTheme={{
                hoverBorder: 'hover:border-emerald-500/50', hoverShadow: 'hover:shadow-[0_20px_40px_rgba(16,185,129,0.1)]',
                bgGradient: 'bg-gradient-to-br from-emerald-500/5 to-transparent', iconBg: 'bg-emerald-500/10', iconText: 'text-emerald-400', iconBorder: 'border-emerald-500/20',
                textGradient: 'from-emerald-400 to-teal-400', borderLeft: 'border-emerald-500/50', cardHoverBorder: 'hover:border-emerald-500/30'
              }}
            />
            <AnalysisCard
              title="G2" icon={Star} mainResult={g2Result} compResult={compG2Result} showCompetitor={showCompetitor} onGeneratePlan={handleGenerateActionPlan}
              colorTheme={{
                hoverBorder: 'hover:border-red-500/50', hoverShadow: 'hover:shadow-[0_20px_40px_rgba(239,68,68,0.1)]',
                bgGradient: 'bg-gradient-to-br from-red-500/5 to-transparent', iconBg: 'bg-red-500/10', iconText: 'text-red-400', iconBorder: 'border-red-500/20',
                textGradient: 'from-red-400 to-rose-400', borderLeft: 'border-red-500/50', cardHoverBorder: 'hover:border-red-500/30'
              }}
            />
            <AnalysisCard
              title="Trustpilot" icon={ThumbsUp} mainResult={trustpilotResult} compResult={compTrustpilotResult} showCompetitor={showCompetitor} onGeneratePlan={handleGenerateActionPlan}
              colorTheme={{
                hoverBorder: 'hover:border-green-500/50', hoverShadow: 'hover:shadow-[0_20px_40px_rgba(34,197,94,0.1)]',
                bgGradient: 'bg-gradient-to-br from-green-500/5 to-transparent', iconBg: 'bg-green-500/10', iconText: 'text-green-400', iconBorder: 'border-green-500/20',
                textGradient: 'from-green-400 to-lime-400', borderLeft: 'border-green-500/50', cardHoverBorder: 'hover:border-green-500/30'
              }}
            />
            <AnalysisCard
              title="Twitter" icon={Twitter} mainResult={twitterResult} compResult={compTwitterResult} showCompetitor={showCompetitor} onGeneratePlan={handleGenerateActionPlan}
              colorTheme={{
                hoverBorder: 'hover:border-slate-400/50', hoverShadow: 'hover:shadow-[0_20px_40px_rgba(148,163,184,0.1)]',
                bgGradient: 'bg-gradient-to-br from-slate-500/5 to-transparent', iconBg: 'bg-slate-500/10', iconText: 'text-slate-300', iconBorder: 'border-slate-500/20',
                textGradient: 'from-slate-300 to-gray-400', borderLeft: 'border-slate-500/50', cardHoverBorder: 'hover:border-slate-500/30'
              }}
            />
          </div>
        )}

        {/* Combine & Deep Insight Section */}
        {isAllAnalysesDone && !deepInsightResult && (
          <div className="flex justify-center mb-12 animate-in fade-in">
            <button
              onClick={handleCombine}
              disabled={isCombining}
              className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-bold py-4 px-10 rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center gap-3"
            >
              {isCombining ? (
                <><Loader2 className="animate-spin" size={20} /> Generating Deep Insight...</>
              ) : (
                <><Zap size={20} /> Combine All & Generate Report</>
              )}
            </button>
          </div>
        )}

        {deepInsightResult && (
          <div className="bg-gradient-to-b from-slate-800 to-slate-900 rounded-2xl p-8 border border-purple-500/30 shadow-[0_0_30px_rgba(168,85,247,0.15)] mb-12 animate-in fade-in slide-in-from-bottom-8">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-700">
              <Lightbulb className="text-purple-400" size={28} />
              <h2 className="text-2xl font-bold text-white">Unified Deep Insight Report</h2>
            </div>

            <div className="mb-8 bg-slate-900/50 p-6 rounded-xl border border-slate-700">
              <h3 className="text-sm uppercase tracking-widest text-purple-400 font-bold mb-3">Overall Summary</h3>
              <p className="text-slate-300 text-lg leading-relaxed">{deepInsightResult.overall_summary}</p>
            </div>

            {deepInsightResult.competitor_ranking && (
              <div className="mb-8 bg-amber-500/10 p-6 rounded-xl border border-amber-500/30">
                <h3 className="text-sm uppercase tracking-widest text-amber-400 font-bold mb-3 flex items-center gap-2">
                  <Target size={16} /> Competitor Ranking
                </h3>
                <p className="text-slate-200 font-bold text-xl">{deepInsightResult.competitor_ranking}</p>
                {deepInsightResult.why_lagging_behind && (
                  <div className="mt-4 pt-4 border-t border-amber-500/20">
                    <h4 className="text-xs uppercase text-amber-500/70 font-bold mb-2">Why Lagging Behind?</h4>
                    <p className="text-slate-300 italic">{deepInsightResult.why_lagging_behind}</p>
                  </div>
                )}
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div>
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <AlertTriangle className="text-orange-400" size={20} /> Key Overarching Gaps
                </h3>
                <div className="space-y-5">
                  {deepInsightResult.key_overarching_gaps?.map((gap, idx) => (
                    <div key={idx} className="bg-slate-800/80 p-5 rounded-xl border border-slate-700/50 hover:border-orange-500/30 transition-colors">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-bold text-white">{gap.title}</h4>
                        <span className={`text-[10px] uppercase px-2 py-1 rounded border ${getSeverityColor(gap.severity)}`}>
                          {gap.severity}
                        </span>
                      </div>
                      <p className="text-sm text-slate-300 mb-3">{gap.description}</p>
                      <div className="text-xs bg-slate-900 p-2 rounded text-slate-400">
                        <span className="font-semibold text-slate-300">Root Cause:</span> {gap.root_cause}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <CheckCircle2 className="text-green-400" size={20} /> Unified Recommendations
                </h3>
                <div className="space-y-3">
                  {deepInsightResult.unified_recommendations?.map((rec, idx) => (
                    <div key={idx} className="flex gap-3 bg-slate-800/80 p-4 rounded-xl border border-slate-700/50">
                      <CheckCircle2 size={18} className="text-green-500 shrink-0 mt-0.5" />
                      <p className="text-sm text-slate-300 leading-relaxed">{rec}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {!proposalResult && (
              <div className="flex justify-center mt-10 pt-8 border-t border-slate-700">
                <button
                  onClick={handleGenerateProposal}
                  disabled={isProposing}
                  className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold py-4 px-10 rounded-xl shadow-lg transition-all flex items-center gap-3"
                >
                  {isProposing ? (
                    <><Loader2 className="animate-spin" size={20} /> Generating Proposal...</>
                  ) : (
                    <><FileText size={20} /> Generate Business Proposal</>
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Proposal Section */}
        {proposalResult && (
          <div className="relative bg-slate-900/60 backdrop-blur-2xl rounded-3xl p-8 md:p-10 border border-emerald-500/40 shadow-[0_0_50px_rgba(16,185,129,0.15)] overflow-hidden animate-in fade-in slide-in-from-bottom-8">
            {/* Background Glow */}
            <div className="absolute top-0 right-0 -mt-20 -mr-20 w-64 h-64 bg-emerald-500/20 rounded-full blur-[100px] pointer-events-none" />
            <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-64 h-64 bg-teal-500/20 rounded-full blur-[100px] pointer-events-none" />

            <div className="relative z-10">
              <div className="flex items-center gap-4 mb-8 pb-6 border-b border-emerald-500/20">
                <div className="p-3 bg-emerald-500/10 rounded-2xl border border-emerald-500/20">
                  <FileText className="text-emerald-400" size={32} />
                </div>
                <div>
                  <h2 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-300">
                    Tailored Business Proposal
                  </h2>
                  <p className="text-emerald-400/60 text-sm font-medium mt-1 uppercase tracking-wider">Strategic Recommendations</p>
                </div>
              </div>

              <div className="mb-10">
                <div className="relative p-6 rounded-2xl bg-gradient-to-r from-emerald-900/20 to-transparent border-l-4 border-emerald-500">
                  <p className="text-slate-200 text-lg md:text-xl leading-relaxed italic font-light">
                    "{proposalResult.proposal_summary}"
                  </p>
                </div>
              </div>

              <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                <span className="w-8 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full"></span>
                Proposed Solutions
              </h3>
              <div className="grid md:grid-cols-2 gap-6 mb-10">
                {proposalResult.proposed_solutions?.map((sol, idx) => (
                  <div key={idx} className="group relative bg-slate-800/40 backdrop-blur-md p-6 rounded-2xl border border-slate-700/50 hover:border-emerald-500/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_10px_30px_rgba(16,185,129,0.1)]">
                    <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl" />

                    <h4 className="font-bold text-emerald-400 text-xl mb-4 flex items-center gap-3">
                      <div className="p-1.5 bg-emerald-500/10 rounded-lg group-hover:bg-emerald-500/20 transition-colors">
                        <CheckCircle2 size={20} className="text-emerald-500" />
                      </div>
                      {sol.service_name}
                    </h4>

                    <div className="mb-4 text-sm text-slate-300">
                      <span className="text-xs uppercase tracking-wider text-slate-500 font-bold block mb-1">Addresses Gap</span>
                      <p className="text-slate-200">{sol.addressed_gap}</p>
                    </div>

                    <div className="text-sm text-slate-300 bg-slate-900/80 p-4 rounded-xl border border-slate-700/50 group-hover:border-emerald-500/20 transition-colors">
                      <span className="text-xs uppercase tracking-wider text-emerald-500/80 font-bold block mb-2">Value Proposition</span>
                      <p className="leading-relaxed">{sol.value_proposition}</p>
                    </div>
                  </div>
                ))}
              </div>

              <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                <span className="w-8 h-1 bg-gradient-to-r from-teal-500 to-emerald-500 rounded-full"></span>
                Next Steps
              </h3>
              <div className="space-y-5">
                {proposalResult.next_steps?.map((step, idx) => (
                  <div key={idx} className="flex gap-5 items-center bg-slate-800/30 backdrop-blur-sm p-5 rounded-2xl border border-slate-700/50 hover:bg-slate-800/50 hover:border-emerald-500/30 transition-all">
                    <div className="w-10 h-10 shrink-0 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-bold shadow-lg shadow-emerald-500/20">
                      {idx + 1}
                    </div>
                    <p className="text-slate-200 font-medium text-lg">{step}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <MarketMatrix targetCompany={companyName} />
        <HistoricalTrends companyName={companyName} />
      </div>

      {/* Action Plan Modal */}
      {activeGapForPlan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm animate-in fade-in">
          <div className="bg-slate-900 border border-emerald-500/30 rounded-2xl p-8 max-w-2xl w-full shadow-[0_20px_60px_rgba(16,185,129,0.2)]">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-white flex items-center gap-2">
                <Zap className="text-emerald-400" /> AI Action Plan
              </h3>
              <button onClick={() => { setActiveGapForPlan(null); setActionPlanResult(null); }} className="text-slate-400 hover:text-white transition-colors">
                <XCircle size={24} />
              </button>
            </div>

            <div className="mb-6 p-4 bg-slate-800/50 rounded-xl border border-slate-700">
              <span className="text-xs uppercase tracking-wider text-slate-500 font-bold">Target Gap</span>
              <h4 className="text-lg font-bold text-slate-200 mt-1">{activeGapForPlan.title}</h4>
            </div>

            {isGeneratingPlan ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 size={40} className="text-emerald-500 animate-spin mb-4" />
                <p className="text-slate-400 font-medium">Synthesizing strategy...</p>
              </div>
            ) : actionPlanResult ? (
              <div className="prose prose-invert prose-emerald max-w-none">
                <div className="bg-emerald-900/20 border border-emerald-500/20 p-6 rounded-xl text-slate-300 leading-relaxed whitespace-pre-wrap">
                  {actionPlanResult}
                </div>
                <div className="mt-8 flex justify-end">
                  <button onClick={() => { setActiveGapForPlan(null); setActionPlanResult(null); }} className="px-6 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-lg transition-colors">
                    Close & Implement
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyAnalysis;
